// src/lib/useSEO.js
// ─────────────────────────────────────────────────────────────────────────────
// Imperatively manages all SEO head tags for a React SPA.
// No external deps — pure DOM manipulation, cleaned up on unmount.
//
// Usage:
//   useSEO({ title, description, canonical, og, jsonLd })
//
// All fields optional — sensible site defaults applied for anything omitted.
// ─────────────────────────────────────────────────────────────────────────────
import { useEffect } from 'react';

// ── Site-wide defaults ────────────────────────────────────────────────────────
export const SITE = {
  name:        'CosmiqHerbz',
  tagline:     'Premium Botanical Collectibles',
  url:         'https://cosmiqherbz.com',     // ← update to your production domain
  description: 'Premium CBD botanical collector specimens. EU-compliant hemp collectibles — flowers, resin, vapes, and bundles. Discreet shipping across Europe.',
  logo:        'https://cosmiqherbz.com/logo.png',
  twitter:     '@cosmiqherbz',                // ← update or remove
  locale:      'en_EU',
  themeColor:  '#0a0a0a',
};

// ── Helper: upsert a <meta> tag by name or property ───────────────────────────
function setMeta(attr, key, content) {
  if (!content) return;
  let el = document.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

// ── Helper: upsert a <link> tag by rel (+ optional hreflang) ─────────────────
function setLink(rel, href, extra = {}) {
  if (!href) return;
  const selector = extra.hreflang
    ? `link[rel="${rel}"][hreflang="${extra.hreflang}"]`
    : `link[rel="${rel}"]`;
  let el = document.querySelector(selector);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
  Object.entries(extra).forEach(([k, v]) => el.setAttribute(k, v));
}

// ── Helper: inject / replace a JSON-LD script ────────────────────────────────
const JSON_LD_ID = 'cosmiq-jsonld';
function setJsonLd(data) {
  let el = document.getElementById(JSON_LD_ID);
  if (!el) {
    el = document.createElement('script');
    el.id = JSON_LD_ID;
    el.type = 'application/ld+json';
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
}
function removeJsonLd() {
  document.getElementById(JSON_LD_ID)?.remove();
}

// ── Main hook ─────────────────────────────────────────────────────────────────
/**
 * @param {object} opts
 * @param {string}  opts.title       — Page title (site name appended automatically)
 * @param {string}  opts.description — Meta description (≤160 chars ideal)
 * @param {string}  opts.canonical   — Canonical path e.g. '/product/abc'
 * @param {object}  opts.og          — Open Graph overrides { title, description, image, type }
 * @param {object|object[]} opts.jsonLd — JSON-LD schema object(s)
 * @param {string}  opts.lang        — BCP47 lang code e.g. 'en', 'de', 'sk'
 * @param {boolean} opts.noIndex     — Set true for admin, /debug, /order-success etc.
 */
export function useSEO({ title, description, canonical, og = {}, jsonLd, lang, noIndex = false } = {}) {
  useEffect(() => {
    const fullTitle  = title ? `${title} | ${SITE.name}` : `${SITE.name} — ${SITE.tagline}`;
    const desc       = description || SITE.description;
    const canonUrl   = canonical ? `${SITE.url}${canonical}` : `${SITE.url}${window.location.pathname}`;
    const ogImage    = og.image  || SITE.logo;
    const ogTitle    = og.title  || fullTitle;
    const ogDesc     = og.description || desc;
    const ogType     = og.type   || 'website';

    // ── <title> ───────────────────────────────────────────────────────────
    document.title = fullTitle;

    // ── <html lang> ───────────────────────────────────────────────────────
    if (lang) document.documentElement.setAttribute('lang', lang);

    // ── Basic meta ────────────────────────────────────────────────────────
    setMeta('name', 'description',   desc);
    setMeta('name', 'theme-color',   SITE.themeColor);
    setMeta('name', 'robots',        noIndex ? 'noindex, nofollow' : 'index, follow');

    // ── Canonical ─────────────────────────────────────────────────────────
    setLink('canonical', canonUrl);

    // ── Open Graph ────────────────────────────────────────────────────────
    setMeta('property', 'og:site_name',   SITE.name);
    setMeta('property', 'og:type',        ogType);
    setMeta('property', 'og:url',         canonUrl);
    setMeta('property', 'og:title',       ogTitle);
    setMeta('property', 'og:description', ogDesc);
    setMeta('property', 'og:image',       ogImage);
    setMeta('property', 'og:locale',      SITE.locale);

    // ── Twitter Card ──────────────────────────────────────────────────────
    setMeta('name', 'twitter:card',        'summary_large_image');
    setMeta('name', 'twitter:site',        SITE.twitter);
    setMeta('name', 'twitter:title',       ogTitle);
    setMeta('name', 'twitter:description', ogDesc);
    setMeta('name', 'twitter:image',       ogImage);

    // ── JSON-LD ───────────────────────────────────────────────────────────
    if (jsonLd) {
      // Wrap array in @graph, or inject single object
      const schema = Array.isArray(jsonLd)
        ? { '@context': 'https://schema.org', '@graph': jsonLd }
        : { '@context': 'https://schema.org', ...jsonLd };
      setJsonLd(schema);
    } else {
      removeJsonLd();
    }

    // Cleanup: remove JSON-LD on unmount to avoid stale data on next page
    return () => removeJsonLd();
  }, [title, description, canonical, lang, noIndex,
      og.image, og.title, og.description, og.type, jsonLd]); // eslint-disable-line
}

// ── Pre-built schema factories ────────────────────────────────────────────────

/** Organization schema — used on every page via WebSite */
export function organizationSchema() {
  return {
    '@type': 'Organization',
    '@id':   `${SITE.url}/#organization`,
    name:    SITE.name,
    url:     SITE.url,
    logo: {
      '@type':      'ImageObject',
      url:          SITE.logo,
      width:        512,
      height:       512,
    },
    sameAs: [
      // Add your social profile URLs here:
      // 'https://www.instagram.com/cosmiqherbz',
      // 'https://www.facebook.com/cosmiqherbz',
    ],
  };
}

/** WebSite schema with SearchAction — enables sitelinks search box in Google */
export function websiteSchema() {
  return {
    '@type':           'WebSite',
    '@id':             `${SITE.url}/#website`,
    url:               SITE.url,
    name:              SITE.name,
    description:       SITE.description,
    publisher:         { '@id': `${SITE.url}/#organization` },
    potentialAction: {
      '@type':       'SearchAction',
      target: {
        '@type':     'EntryPoint',
        urlTemplate: `${SITE.url}/search?q={search_term_string}`,
      },
      'query-input': 'required name=search_term_string',
    },
  };
}

/** Product schema for a single product page */
export function productSchema(product, lang = 'en') {
  const name  = product?.name?.[lang] || product?.name?.en || 'Product';
  const desc  = product?.description?.[lang] || product?.description?.en || '';
  const image = product?.images?.[0];
  const price = product?.price_eur;
  const inStock = (product?.stock || 0) > 0;

  return {
    '@type':       'Product',
    '@id':         `${SITE.url}/product/${product?.id}`,
    name,
    description:   desc,
    image:         image ? [image] : undefined,
    sku:           product?.sku || product?.id,
    brand: {
      '@type': 'Brand',
      name:    SITE.name,
    },
    offers: {
      '@type':           'Offer',
      url:               `${SITE.url}/product/${product?.id}`,
      priceCurrency:     'EUR',
      price:             price?.toFixed(2),
      availability:      inStock
        ? 'https://schema.org/InStock'
        : 'https://schema.org/OutOfStock',
      itemCondition:     'https://schema.org/NewCondition',
      seller: {
        '@type': 'Organization',
        name:    SITE.name,
      },
    },
    ...(product?.cbd_percent ? {
      additionalProperty: [
        { '@type': 'PropertyValue', name: 'CBD Content', value: `${product.cbd_percent}%` },
        { '@type': 'PropertyValue', name: 'THC Content', value: `${product.thc_percent || 0}%` },
      ],
    } : {}),
  };
}

/** BreadcrumbList schema */
export function breadcrumbSchema(crumbs) {
  // crumbs: [{ name: 'Home', path: '/' }, { name: 'CBD Flower', path: '/search' }, ...]
  return {
    '@type':           'BreadcrumbList',
    itemListElement:   crumbs.map((c, i) => ({
      '@type':    'ListItem',
      position:   i + 1,
      name:       c.name,
      item:       `${SITE.url}${c.path}`,
    })),
  };
}

/** CollectionPage schema for category/home sections */
export function collectionPageSchema(name, description, url) {
  return {
    '@type':       'CollectionPage',
    '@id':         `${SITE.url}${url}`,
    name,
    description,
    url:           `${SITE.url}${url}`,
    publisher:     { '@id': `${SITE.url}/#organization` },
  };
}
